import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeacherStudentTracking = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentJourney, setStudentJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [journeyLoading, setJourneyLoading] = useState(false);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/teacher/students`, { withCredentials: true });
      setStudents(response.data.students || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentJourney = async (studentId) => {
    setJourneyLoading(true);
    try {
      const response = await axios.get(`${API}/teacher/student-journey/${studentId}`, { withCredentials: true });
      setStudentJourney(response.data);
      setSelectedStudent(studentId);
    } catch (error) {
      console.error('Error fetching student journey:', error);
      setStudentJourney(null);
    } finally {
      setJourneyLoading(false);
    }
  };

  const getPerformanceColor = (rate) => {
    if (rate >= 0.8) return 'text-green-600 bg-green-100';
    if (rate >= 0.6) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Öğrenci Takibi' : 'Student Tracking'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl shadow-2xl p-8 mb-8 text-white">
          <h1 className="text-4xl font-bold mb-2">
            👥 {language === 'tr' ? 'Öğrenci Takibi' : 'Student Tracking'}
          </h1>
          <p className="text-purple-100 text-lg">
            {language === 'tr' 
              ? 'Öğrencilerinizin öğrenme yolculuğunu takip edin'
              : 'Track your students learning journey'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Student List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                {language === 'tr' ? '📚 Öğrencilerim' : '📚 My Students'}
              </h2>
              
              {students.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>{language === 'tr' ? 'Henüz öğrenci yok' : 'No students yet'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {students.map((student) => (
                    <div
                      key={student.id}
                      onClick={() => fetchStudentJourney(student.id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedStudent === student.id
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold">
                          {student.name?.charAt(0)?.toUpperCase() || 'S'}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-800">{student.name || 'Student'}</h3>
                          <p className="text-xs text-gray-500">{student.email}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Student Journey Details */}
          <div className="lg:col-span-2">
            {!selectedStudent ? (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <div className="text-6xl mb-4">👈</div>
                <p className="text-gray-600 text-lg">
                  {language === 'tr' 
                    ? 'Detayları görmek için bir öğrenci seçin'
                    : 'Select a student to view details'}
                </p>
              </div>
            ) : journeyLoading ? (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-gray-600">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
              </div>
            ) : !studentJourney ? (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <div className="text-6xl mb-4">📊</div>
                <p className="text-gray-600 text-lg">
                  {language === 'tr' 
                    ? 'Bu öğrenci için henüz veri yok'
                    : 'No data available for this student'}
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl shadow-lg p-4">
                    <div className="text-2xl font-bold text-blue-600">
                      {studentJourney.total_study_hours?.toFixed(1) || 0}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {language === 'tr' ? 'Toplam Saat' : 'Total Hours'}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-lg p-4">
                    <div className="text-2xl font-bold text-green-600">
                      {((studentJourney.overall_accuracy || 0) * 100).toFixed(0)}%
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {language === 'tr' ? 'Başarı' : 'Accuracy'}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-lg p-4">
                    <div className="text-2xl font-bold text-purple-600">
                      {studentJourney.total_questions_attempted || 0}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {language === 'tr' ? 'Soru' : 'Questions'}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-lg p-4">
                    <div className="text-2xl font-bold text-orange-600">
                      {studentJourney.assigned_tasks?.filter(t => t.completed).length || 0}
                      <span className="text-sm text-gray-500">
                        /{studentJourney.assigned_tasks?.length || 0}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {language === 'tr' ? 'Görev' : 'Tasks'}
                    </div>
                  </div>
                </div>

                {/* Topics Performance */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">
                    📚 {language === 'tr' ? 'Konu Performansı' : 'Topic Performance'}
                  </h3>
                  
                  {studentJourney.topics && studentJourney.topics.length > 0 ? (
                    <div className="space-y-3">
                      {studentJourney.topics
                        .sort((a, b) => b.correct_rate - a.correct_rate)
                        .slice(0, 10)
                        .map((topic, idx) => (
                          <div key={idx} className="border rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-gray-800 text-sm">{topic.topic}</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-bold ${getPerformanceColor(topic.correct_rate)}`}>
                                {(topic.correct_rate * 100).toFixed(0)}%
                              </span>
                            </div>
                            
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                                style={{ width: `${topic.correct_rate * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <p className="text-gray-600 text-center py-4">
                      {language === 'tr' ? 'Henüz konu verisi yok' : 'No topic data yet'}
                    </p>
                  )}
                </div>

                {/* Weak Areas */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">
                    🎯 {language === 'tr' ? 'Geliştirilmesi Gerekenler' : 'Areas to Improve'}
                  </h3>
                  
                  {studentJourney.topics?.filter(t => t.correct_rate < 0.6).length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {studentJourney.topics
                        .filter(t => t.correct_rate < 0.6)
                        .map((topic, idx) => (
                          <span
                            key={idx}
                            className="px-4 py-2 bg-red-100 text-red-800 rounded-full text-sm font-semibold"
                          >
                            {topic.topic} ({(topic.correct_rate * 100).toFixed(0)}%)
                          </span>
                        ))}
                    </div>
                  ) : (
                    <p className="text-gray-600">{language === 'tr' ? 'Harika gidiyor! 🎉' : 'Great performance! 🎉'}</p>
                  )}
                </div>

                {/* Recent Tasks */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">
                    ✅ {language === 'tr' ? 'Son Görevler' : 'Recent Tasks'}
                  </h3>
                  
                  {studentJourney.assigned_tasks && studentJourney.assigned_tasks.length > 0 ? (
                    <div className="space-y-3">
                      {studentJourney.assigned_tasks.slice(0, 5).map((task, idx) => (
                        <div
                          key={idx}
                          className={`border rounded-lg p-3 ${
                            task.completed ? 'bg-green-50 border-green-200' : 'bg-white'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span className="text-xl">
                                {task.completed ? '✅' : '⏳'}
                              </span>
                              <span className="font-semibold text-gray-800 text-sm">
                                {task.title || task.topic}
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                                {task.difficulty}
                              </span>
                              <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">
                                {task.estimated_minutes || task.estimatedMinutes} min
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600 text-center py-4">
                      {language === 'tr' ? 'Henüz görev yok' : 'No tasks yet'}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherStudentTracking;
